import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef } from 'react';
import { Search, ChevronDown, Check } from 'lucide-react';
const SearchableSelect = ({ label, placeholder = "Pesquisar...", options, value, onChange, onSearch, isLoading = false, className = "", disabled = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredOptions, setFilteredOptions] = useState(options);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);
    const listRef = useRef(null);
    // Filtrar opções localmente se não houver onSearch
    useEffect(() => {
        if (!onSearch) {
            const filtered = options.filter(option => option.label.toLowerCase().includes(searchTerm.toLowerCase()));
            setFilteredOptions(filtered);
        }
        else {
            setFilteredOptions(options);
        }
        setHighlightedIndex(-1);
    }, [options, searchTerm, onSearch]);
    // Pesquisa externa
    useEffect(() => {
        if (onSearch && searchTerm.trim().length >= 2) {
            const timeoutId = setTimeout(() => {
                onSearch(searchTerm.trim());
            }, 500); // Debounce de 500ms
            return () => clearTimeout(timeoutId);
        }
        else if (onSearch && searchTerm.trim().length < 2) {
            // Limpar resultados se menos de 2 caracteres
            setFilteredOptions([]);
        }
    }, [searchTerm, onSearch]);
    // Fechar dropdown quando clicar fora
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);
    // Navegação por teclado
    const handleKeyDown = (e) => {
        if (!isOpen) {
            if (e.key === 'Enter' || e.key === 'ArrowDown') {
                setIsOpen(true);
                setHighlightedIndex(0);
                e.preventDefault();
            }
            return;
        }
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex(prev => prev < filteredOptions.length - 1 ? prev + 1 : 0);
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex(prev => prev > 0 ? prev - 1 : filteredOptions.length - 1);
                break;
            case 'Enter':
                e.preventDefault();
                if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
                    handleSelectOption(filteredOptions[highlightedIndex]);
                }
                break;
            case 'Escape':
                setIsOpen(false);
                setSearchTerm('');
                inputRef.current?.blur();
                break;
        }
    };
    // Scroll para o item destacado
    useEffect(() => {
        if (listRef.current && highlightedIndex >= 0) {
            const highlightedElement = listRef.current.children[highlightedIndex];
            if (highlightedElement) {
                highlightedElement.scrollIntoView({
                    block: 'nearest'
                });
            }
        }
    }, [highlightedIndex]);
    const handleSelectOption = (option) => {
        onChange(option.value, option);
        setIsOpen(false);
        setSearchTerm('');
        inputRef.current?.blur();
    };
    const selectedOption = options.find(opt => opt.value === value);
    return (_jsxs("div", { className: `relative ${className}`, ref: dropdownRef, children: [label && (_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-1", children: label })), _jsxs("div", { className: "relative", children: [_jsx("div", { className: `
            w-full px-3 py-2 border border-gray-300 rounded-md bg-white cursor-pointer
            ${disabled ? 'bg-gray-50 cursor-not-allowed' : 'hover:border-gray-400'}
            ${isOpen ? 'border-blue-500 ring-1 ring-blue-500' : ''}
            transition-colors duration-200
          `, onClick: () => !disabled && setIsOpen(!isOpen), children: _jsxs("div", { className: "flex items-center justify-between", children: [_jsxs("div", { className: "flex items-center gap-2 flex-1", children: [_jsx(Search, { className: "h-4 w-4 text-gray-400" }), isOpen ? (_jsx("input", { ref: inputRef, type: "text", value: searchTerm, onChange: (e) => setSearchTerm(e.target.value), onKeyDown: handleKeyDown, placeholder: placeholder, className: "flex-1 outline-none bg-transparent text-sm", autoFocus: true, disabled: disabled })) : (_jsx("span", { className: `flex-1 text-sm ${selectedOption ? 'text-gray-900' : 'text-gray-500'}`, children: selectedOption ? selectedOption.label : placeholder }))] }), _jsx(ChevronDown, { className: `h-4 w-4 text-gray-400 transform transition-transform ${isOpen ? 'rotate-180' : ''}` })] }) }), isOpen && (_jsx("div", { className: "absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto", children: isLoading ? (_jsxs("div", { className: "px-3 py-2 text-sm text-gray-500 text-center", children: [_jsx("div", { className: "animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mx-auto" }), _jsx("span", { className: "mt-1 block", children: "Pesquisando..." })] })) : filteredOptions.length === 0 ? (_jsx("div", { className: "px-3 py-2 text-sm text-gray-500 text-center", children: searchTerm ? 'Nenhum resultado encontrado' : 'Nenhuma opção disponível' })) : (_jsx("div", { ref: listRef, children: filteredOptions.map((option, index) => (_jsxs("div", { className: `
                      px-3 py-2 cursor-pointer text-sm flex items-center justify-between
                      ${index === highlightedIndex ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50'}
                      ${option.value === value ? 'bg-blue-100 text-blue-700' : ''}
                    `, onClick: () => handleSelectOption(option), onMouseEnter: () => setHighlightedIndex(index), children: [_jsx("span", { children: option.label }), option.value === value && (_jsx(Check, { className: "h-4 w-4 text-blue-600" }))] }, option.value))) })) }))] })] }));
};
export default SearchableSelect;
